import { show } from "@ebay/nice-modal-react";
import { canv } from "@/canv/global";
import { getTarget, sleep } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";
import { t } from "@/i18n";
import { BuyProModal } from "@/interface/ui/modals/BuyProModal";
import { showSnackbar } from "@/snackbar/snackbar";
import { BaseExport } from "./BaseExport";
import { CROWN_ICON } from "./constants";
import { collectFrameGroups } from "./utils";
import { VectorExport } from "./VectorExport";

type ExportState = {
    type: "base" | "vector" | null;
    structure: "page" | "pages" | null;
};

export class ExportWindow {
    constructor() {
        this.baseExport = new BaseExport();
        this.vectorExport = new VectorExport();
    }

    open() {
        if (document.querySelector(".exportWindowWrapper")) {
            this.close();
        }

        const exportWindowWrapper = document.createElement("div");
        exportWindowWrapper.classList.add("exportWindowWrapper");
        document.body.prepend(exportWindowWrapper);
        this.initWrapper();

        const exportWindow = document.createElement("div");
        exportWindow.classList.add("exportWindow");
        exportWindowWrapper.prepend(exportWindow);

        const exportButton = document.getElementsByClassName("barPanelExport")[0];
        if (exportButton) {
            const buttonRect = exportButton.getBoundingClientRect();
            const windowRect = exportWindow.getBoundingClientRect();
            let leftPos = buttonRect.right - windowRect.width;
            // Минимальное значение left = 68px
            if (leftPos < 68) {
                leftPos = 68;
            }

            exportWindow.style.left = `${leftPos}px`;
        }

        const { groupsWithFrame, groupsWithoutFrame } = collectFrameGroups(
            canv.canvasController.getSelected(),
        );

        this.groupsWithFrame = groupsWithFrame;
        this.groupsWithoutFrame = groupsWithoutFrame;
        this.state.type = "base";
        this.state.structure = "page";

        this.draw();
        this.init();

        eventEmitter.dispatch("header.button.active.name", {
            name: "export",
        });
    }

    initWrapper() {
        const exportWindowWrapper = document.querySelector(".exportWindowWrapper");
        exportWindowWrapper?.addEventListener("mousedown", this.wrapperClick);
    }

    wrapperClick = (e) => {
        const tryInner = getTarget(e, "exportWindow");
        if (tryInner == null) {
            this.close();
        }
    };

    close() {
        const exportWindow = document.querySelector(".exportWindow");
        if (exportWindow) {
            exportWindow.removeEventListener("change", this.changeExportOptions);
            exportWindow.removeEventListener("click", this.onClickProOption);
        }

        const exportWindowDownloadButton = document.querySelector(".exportWindowDownloadButton");
        if (exportWindowDownloadButton) {
            exportWindowDownloadButton.removeEventListener("mouseup", this.onClickExport);
        }

        const exportWindowWrapper = document.querySelector(".exportWindowWrapper");
        if (exportWindowWrapper) {
            exportWindowWrapper.outerHTML = "";
        }

        eventEmitter.dispatch("header.button.active.name", {
            name: null,
        });
    }

    init() {
        const exportWindow = document.querySelector(".exportWindow");
        if (exportWindow) {
            exportWindow.addEventListener("change", this.changeExportOptions);
            exportWindow.addEventListener("click", this.onClickProOption);
        }

        const exportWindowDownloadButton = document.querySelector(".exportWindowDownloadButton");
        if (exportWindowDownloadButton) {
            exportWindowDownloadButton.addEventListener("mouseup", this.onClickExport);
        }
    }

    onClickProOption = (e: Event) => {
        if (!(e.target instanceof Element) || !e.target.closest("[data-buy-pro]")) {
            return;
        }

        show(BuyProModal, { feature: "export" });
    };

    changeExportOptions = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target) {
            return;
        }

        if (target.name === "type") {
            this.state.type = target.value as ExportState["type"];
            this.updateFileType();
            this.updateFileStructure();
        } else if (target.name === "structure") {
            this.state.structure = target.value as ExportState["structure"];
            this.updateFileStructure();
        }
    };

    updateFileType() {
        const exportWindow = document.querySelector(".exportWindow");
        const fileTypeElement = exportWindow?.querySelector(".exportWindowFileType");
        if (!fileTypeElement) {
            return;
        }

        fileTypeElement.innerHTML = this.renderFileType();
    }

    updateFileStructure() {
        const exportWindow = document.querySelector(".exportWindow");
        const fileStructureElement = exportWindow?.querySelector(".exportWindowFileStructure");
        if (!fileStructureElement) {
            return;
        }

        fileStructureElement.innerHTML = this.renderFileStructure();
    }

    draw() {
        const exportWindow = document.querySelector(".exportWindow");
        if (!exportWindow) {
            return;
        }

        const downloadText = this.groupsWithFrame.length
            ? t("export.downloadSelected", { count: this.groupsWithFrame.length })
            : t("export.download");

        exportWindow.innerHTML = `
            <div class="exportWindowTitle">${t("export.params")}</div>
            <hr class="exportWindowDivider" />

            <div class="exportWindowFileType">
                ${this.renderFileType()}
            </div>

            <div class="exportWindowFileStructure">
                ${this.renderFileStructure()}
            </div>

            <div class="exportWindowBottom">
                <button class="exportWindowDownloadButton">${downloadText}</button>
            </div>
    `;
    }

    renderFileType() {
        return `
            <div class="exportWindowSubtitle">${t("export.fileType")}</div>
            ${this.renderRadio("type", "base", "PDF")}
            ${this.renderRadio("type", "vector", t("export.pdfHighRes"), undefined, true)}
    `;
    }

    renderFileStructure() {
        if (
            this.groupsWithFrame.length < 2 ||
            !(this.state.type === "base" || this.state.type === "vector")
        ) {
            return "";
        }

        return `
            <div class="exportWindowSubtitle">${t("export.fileStructure")}</div>
            ${this.renderRadio("structure", "page", t("export.singlePage"))}
            ${this.renderRadio("structure", "pages", t("export.pagePerFrame"), t("export.pagePerFrameDescription"), true)}
    `;
    }

    renderRadio(name: string, value: string, label: string, description?: string, proOnly = false) {
        const checked = this.state[name] === value ? "checked" : "";
        const isLocked = proOnly && !this.isProfessional();
        const disabled = isLocked ? "disabled" : "";
        const proIcon = isLocked ? CROWN_ICON : "";

        return `
            <div class="exportWindowOption">
                <label>
                    <input type="radio" name="${name}" value="${value}" ${checked} ${disabled}>
                    <span class="exportWindowOptionContent">
                        <span>${label}</span>
                        ${proIcon}
                    </span>
                </label>
                ${this.state[name] === value && description ? `<p class="exportWindowOptionDescription">${description}</p>` : ""}
            </div>
        `;
    }

    onClickExport = async () => {
        if (!(this.state.type && this.state.structure)) {
            return;
        }

        if (this.isPaidExportState()) {
            return;
        }

        this.close();

        eventEmitter.dispatch("button.disabled", {
            export: true,
        });
        canv.multiManager.changeVisibilityCursor(false);

        const loadingSnackbar = showSnackbar(t("export.loading"), {
            type: "loading",
        });

        // Даем браузеру время на обновление UI перед началом вычислений
        await sleep(0);

        try {
            await this.exportByState();
            showSnackbar(t("export.success"), {
                duration: SNACKBAR_TIME,
                type: "success",
            });
        } catch {
            showSnackbar(t("export.error"), { type: "error", duration: SNACKBAR_TIME });
        } finally {
            loadingSnackbar.close();
            eventEmitter.dispatch("button.disabled", {
                export: false,
            });

            if (!canv.multiManager.disableMultiCursors) {
                canv.multiManager.changeVisibilityCursor(true);
            }
        }
    };

    async exportByState() {
        if (!(this.state.type && this.state.structure)) {
            return;
        }

        const exportQuality = this.state.type === "vector" ? this.vectorExport : this.baseExport;

        if (!this.groupsWithFrame.length) {
            return await exportQuality.exportViewport();
        }

        if (this.state.structure === "page") {
            return await exportQuality.exportPage(this.groupsWithFrame, this.groupsWithoutFrame);
        }

        if (this.state.structure === "pages") {
            return await exportQuality.exportPages(this.groupsWithFrame, this.groupsWithoutFrame);
        }
    }

    private isProfessional() {
        return canv.getInitDataProp("isProfessional");
    }

    private isPaidExportState() {
        return (
            !this.isProfessional() &&
            (this.state.type === "vector" || this.state.structure === "pages")
        );
    }

    private groupsWithFrame: Group[] = [];
    private groupsWithoutFrame: Group[] = [];
    private state: ExportState = { type: null, structure: null };
    private readonly baseExport: BaseExport;
    private readonly vectorExport: VectorExport;
}
